/**
 * The student's boot client: session (auth-js), queries (postgrest-js) and functions (functions-js) — the parts the
 * boot uses — wired exactly as supabase-js 2.97.0 wires them (dist/index.mjs:107-118, 225-245, 336-357). Realtime
 * and storage arrive later through `lite.ts loadSupabaseExtras` (spec 2026-10-14_student-lite-supabase-boot).
 */
import { AuthClient, processLock } from '@supabase/auth-js';
import { PostgrestClient } from '@supabase/postgrest-js';
import { FunctionsClient } from '@supabase/functions-js';

export interface LiteConfig {
    readonly url: string;
    readonly anonKey: string;
    readonly storageKey: string;
}

export interface LiteCore {
    /* `AuthClient` from `@supabase/auth-js` is declared as `const AuthClient: typeof GoTrueClient` —
       a value, not a type — so the instance type is spelled with `InstanceType<typeof AuthClient>`. */
    readonly auth: InstanceType<typeof AuthClient>;
    readonly rest: PostgrestClient;
    readonly functions: FunctionsClient;
    accessToken(): Promise<string | null>;
}

/** Same header as supabase-js 2.97.0 sends, so server logs read the same. */
const CLIENT_INFO: Readonly<Record<string, string>> = { 'X-Client-Info': 'supabase-js-web/2.97.0' };

export function fetchWithAuth(anonKey: string, getAccessToken: () => Promise<string | null>): typeof fetch {
    return async (input, init) => {
        const token = (await getAccessToken()) ?? anonKey;
        const headers = new Headers(init?.headers);
        if (!headers.has('apikey')) headers.set('apikey', anonKey);
        if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
        return fetch(input, { ...init, headers });
    };
}

export function createLiteCore(config: LiteConfig): LiteCore {
    const base = config.url.replace(/\/+$/, '');
    const auth = new AuthClient({
        url: `${base}/auth/v1`,
        headers: { Authorization: `Bearer ${config.anonKey}`, apikey: config.anonKey, ...CLIENT_INFO },
        storageKey: config.storageKey,
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
        flowType: 'pkce',
        lock: processLock,
        debug: false,
    });
    const accessToken = async (): Promise<string | null> => (await auth.getSession()).data.session?.access_token ?? null;
    const authed = fetchWithAuth(config.anonKey, accessToken);
    const rest = new PostgrestClient(`${base}/rest/v1`, { headers: { ...CLIENT_INFO }, schema: 'public', fetch: authed });
    const functions = new FunctionsClient(`${base}/functions/v1`, { headers: { ...CLIENT_INFO }, customFetch: authed });
    return { auth, rest, functions, accessToken };
}
